// src/pages/Login/ResetPassword.js
import AuthLayout from 'components/layout/AuthLayout'
import { labelClass, linkClass, submitButtonClass, errorAlertClass, successAlertClass } from 'forms/Login/authStyles'
import PasswordField from 'forms/Login/PasswordField'
import { Form, Formik } from 'formik'
import useFetch from 'hooks/useFetch'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'wouter'
import * as Yup from 'yup'

const PASSWORD_MIN_LENGTH = 6

// Extrae el "code" del cuerpo de error que devuelve useFetch (texto JSON)
const errorCode = (error) => {
  try {
    return JSON.parse(error)?.code
  } catch {
    return null
  }
}

export default function ResetPassword() {
  const { t } = useTranslation()
  const [, setLocation] = useLocation()
  const [, validateToken] = useFetch()
  const [resetState, resetPassword] = useFetch()

  const token = new URLSearchParams(window.location.search).get('token') || ''
  // checking | valid | invalid | done
  const [status, setStatus] = useState(token ? 'checking' : 'invalid')
  const [submitError, setSubmitError] = useState(null)

  useEffect(() => {
    if (!token) return
    validateToken({ url: `auth/reset-password/validate?token=${encodeURIComponent(token)}`, method: 'GET' })
      .then(result => setStatus(result?.valid ? 'valid' : 'invalid'))
  }, []) // eslint-disable-line

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required(t('password_reset.v_password_required'))
      .min(PASSWORD_MIN_LENGTH, t('password_reset.v_password_min')),
    passwordRepeat: Yup.string()
      .required(t('password_reset.v_password_required'))
      .oneOf([Yup.ref('password')], t('password_reset.v_passwords_match'))
  })

  const handleSubmit = async (values) => {
    setSubmitError(null)
    const result = await resetPassword({
      url: 'auth/reset-password',
      method: 'POST',
      body: { token, password: values.password }
    })
    if (!result?.error) {
      setStatus('done')
    } else if (errorCode(result.error) === 'invalid_or_expired') {
      setStatus('invalid')
    } else {
      setSubmitError(t('password_reset.generic_error'))
    }
  }

  const goTo = (path) => (e) => { e.preventDefault(); setLocation(path) }

  return (
    <AuthLayout subtitle={t('password_reset.reset_subtitle')}>
      {status === 'checking' && (
        <p className="text-sm text-center text-gray-500">{t('password_reset.checking')}</p>
      )}

      {status === 'invalid' && (
        <>
          <div className={errorAlertClass} role="alert">
            {t('password_reset.link_invalid')}
          </div>
          <div className="mt-5">
            <button type="button" className={submitButtonClass} onClick={goTo('/forgot-password')}>
              {t('password_reset.request_new')}
            </button>
          </div>
        </>
      )}

      {status === 'done' && (
        <>
          <div className={successAlertClass} role="status">
            {t('password_reset.done')}
          </div>
          <div className="mt-5">
            <button type="button" className={submitButtonClass} onClick={goTo('/login')}>
              {t('password_reset.go_to_login')}
            </button>
          </div>
        </>
      )}

      {status === 'valid' && (
        <Formik
          initialValues={{ password: '', passwordRepeat: '' }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched }) => (
            <Form>
              <label htmlFor="password" className={labelClass(touched.password && errors.password)}>
                {t('password_reset.new_password')}
              </label>
              <PasswordField
                id="password"
                name="password"
                hasError={!!(touched.password && errors.password)}
                autoComplete="new-password"
              />
              <p className={`mt-1 text-sm leading-none text-red-600 ${touched.password && errors.password ? 'visible' : 'invisible'}`}>
                {errors.password || ' '}
              </p>

              <div className="mt-4">
                <label htmlFor="passwordRepeat" className={labelClass(touched.passwordRepeat && errors.passwordRepeat)}>
                  {t('password_reset.repeat_password')}
                </label>
                <PasswordField
                  id="passwordRepeat"
                  name="passwordRepeat"
                  hasError={!!(touched.passwordRepeat && errors.passwordRepeat)}
                  autoComplete="new-password"
                />
                <p className={`mt-1 text-sm leading-none text-red-600 ${touched.passwordRepeat && errors.passwordRepeat ? 'visible' : 'invisible'}`}>
                  {errors.passwordRepeat || ' '}
                </p>
              </div>

              <div className="mt-5">
                <button type="submit" className={submitButtonClass} disabled={resetState.loading}>
                  {resetState.loading ? t('password_reset.saving') : t('password_reset.save_password')}
                </button>
              </div>

              {submitError && (
                <div className={`${errorAlertClass} mt-3`} role="alert">
                  {submitError}
                </div>
              )}
            </Form>
          )}
        </Formik>
      )}

      <div className="mt-6 text-center">
        <a href="/login" onClick={goTo('/login')} className={linkClass}>
          {t('password_reset.back_to_login')}
        </a>
      </div>
    </AuthLayout>
  )
}
